import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { twoFactorAPI } from '../lib/api';
import { Mail, Lock, LogIn, Loader2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function LoginPage() {
  const { login, auth0Login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed';
      if (detail.includes('frozen')) {
        setError('Your account has been frozen. Please contact support.');
      } else {
        setError(detail);
      }
    }
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await twoFactorAPI.verify({ email, password, code: twoFACode });
      if (res.data.token) {
        localStorage.setItem('xdx_token', res.data.token);
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    }
    setLoading(false);
  };

  if (requires2FA) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Shield className="h-12 w-12 text-[#00E5FF] mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight" data-testid="2fa-title">Two-Factor Authentication</h1>
            <p className="text-zinc-500 text-sm mt-2">Enter the code from your authenticator app</p>
          </div>

          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8">
            <form onSubmit={handle2FASubmit} className="space-y-5">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Verification Code</Label>
                <Input
                  type="text"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-1.5 h-14 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  data-testid="2fa-code-input"
                />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="2fa-error">{error}</p>}
              <Button type="submit" disabled={loading}
                className="w-full h-12 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="2fa-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Login'}
              </Button>
            </form>
            <button onClick={() => { setRequires2FA(false); setTwoFACode(''); setError(''); }}
              className="w-full mt-4 text-sm text-zinc-500 hover:text-[#00E5FF]">
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight" data-testid="login-title">{t('nav.login')}</h1>
          <p className="text-zinc-500 text-sm mt-2">Sign in to your XDomainX account</p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.email')}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="login-email-input"
                  placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.password')}</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="login-password-input"
                  placeholder="Enter password" />
              </div>
              <div className="flex justify-end mt-1.5">
                <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-[#00E5FF] transition-colors" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-2" /> {t('nav.login')}</>}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <span className="relative px-4 text-xs text-zinc-600 bg-[#0A0A0A]">or continue with</span>
            </div>
            <div className="space-y-3 mt-4">
              <Button onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }} variant="outline"
                className="w-full h-12 border-zinc-800 hover:border-[#00E5FF] text-zinc-300 hover:text-[#00E5FF] rounded-sm uppercase tracking-wider" data-testid="google-login-btn">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Login with Google
              </Button>
              <Button onClick={auth0Login} variant="outline"
                className="w-full h-12 border-zinc-800 hover:border-[#00E5FF] text-zinc-300 hover:text-[#00E5FF] rounded-sm uppercase tracking-wider" data-testid="auth0-login-btn">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M21.98 7.448L19.62 0H4.347L2.02 7.448c-1.352 4.312.03 9.206 3.815 12.015L12.007 24l6.157-4.552c3.755-2.81 5.182-7.688 3.815-12.015l-1.97-2.41M12.007 17.89l-4.584-3.39L4.7 7.87l2.57-3.39h9.477l2.57 3.39-2.723 6.63z"/></svg>
                Login with Auth0
              </Button>
            </div>
          </div>

          <p className="text-center text-sm text-zinc-500 mt-6">
            Don't have an account? <Link to="/register" className="text-[#00E5FF] hover:underline" data-testid="register-link">{t('nav.register')}</Link>
          </p>
        </div>

        {/* Demo credentials hint */}
        <div className="mt-4 p-3 bg-[#0A0A0A] border border-white/5 rounded-sm">
          <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Demo Credentials</p>
          <p className="text-xs text-zinc-500">Admin: admin@xdomainx.com / admin123</p>
          <p className="text-xs text-zinc-500">User: john@example.com / password123</p>
        </div>
      </div>
    </div>
  );
}
